import { useCompanyDataServiceV2 } from "@rw1/api-services";
import { CompanyData_V2 } from "@rw1/auto-generated-clients";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { STALE_TIME_MS } from "./constants";

const COMPANY_DATA_QUERY_KEY = "companyData";

const demoSuffix = ".rwone.com";

interface UseCompanyDataResult {
  company: CompanyData_V2.Company | undefined;
  isLoading: boolean;
  error: Error | null;
  isSsoCompany: boolean;
  demoEmailDomain: string;
}

export const useCompanyData = (companyId?: string, isEnabled?: boolean): UseCompanyDataResult => {
  const companyDataClient = useCompanyDataServiceV2();

  const {
    data: company,
    isLoading,
    error,
  } = useQuery({
    queryKey: [COMPANY_DATA_QUERY_KEY, companyId],
    queryFn: () => {
      if (!companyId) throw new Error("Company ID is required");

      return companyDataClient.getCompanyById(companyId);
    },
    enabled: !!isEnabled && !!companyId,
    staleTime: STALE_TIME_MS,
  });

  return useMemo(() => {
    const isSsoCompany = !!company?.sSODomain && company.sSODomain.length > 0;
    const demoEmailDomain = company?.companyName
      ? `@${company.companyName}${demoSuffix}`
      : "";

    return {
      company,
      isLoading: isLoading || (!!companyId && company === undefined && !error),
      error,
      isSsoCompany,
      demoEmailDomain,
    };
  }, [company, isLoading, error, companyId]);
};
